import { createSlice } from '@reduxjs/toolkit';

import { GetAmplitudeFeatureFlagsRequest_variant_getFeatureFlags_variants } from 'src/api/queries/__generated__/GetAmplitudeFeatureFlagsRequest';
import { GetSellerInputQuery_reception_getSellerInput_sellerInput_featureFlagsAmplitude } from 'src/api/queries/__generated__/GetSellerInputQuery';

import { api } from './api';

// Add feature flags here, if possible try to make it so feature flags can
// safely default to false, otherwise you will need to modify the code for
// setting the initialFeatureFlagState accordingly
//
// This is a map from nice
// JavascriptName: amplitude_key_returned_in_sellerInput.featureFlagsAmplitude
const featureFlagMap = {
  isInListWithCertaintyTreatment: 'seller-23-06-longterm-list-with-certainty-enrollment',
  isInHideMarketingAttributionTreatment: 'hide-marketing-attribution-question',
  isInAgentReferralByInvitationPilot: 'partnership-agent-referral-by-invitation',
  novoAuthStyle: 'core-exp-2024-04-novo-auth',
};

/**
 * Format the feature flags map so that it can passed to GetSellerInputQuery
 * (See usage in store/api.ts)
 */
export const getFeatureFlagNames = (): Array<string> => {
  return Object.entries(featureFlagMap).map(([_key, value]) => value);
};

const getFlagFromLocalStorage = (key: string): boolean | undefined => {
  const flag = window.localStorage.getItem(`opendoor.reception.${key}`);
  if (flag === 'true') {
    return true;
  }
  if (flag === 'false') {
    return false;
  }
  return undefined;
};

export type FeatureFlags = Record<keyof typeof featureFlagMap, boolean>;
export const initialFeatureFlagState: FeatureFlags = Object.keys(featureFlagMap).reduce(
  (allFlags, key) => {
    allFlags[key] = false;
    return allFlags;
  },
  {},
) as FeatureFlags;

const mapFlags = (
  flags: Array<GetSellerInputQuery_reception_getSellerInput_sellerInput_featureFlagsAmplitude> | null,
) => {
  if (flags) {
    return flags.reduce((allFlags, entry) => {
      if (entry) {
        allFlags[entry.name] = entry.enabled;
      }
      return allFlags;
    }, {} as Record<string, boolean>);
  }
  return {};
};

const mapVariantFlags = (
  flags: Array<GetAmplitudeFeatureFlagsRequest_variant_getFeatureFlags_variants> | null,
) => {
  if (flags) {
    return flags.reduce((allFlags, entry) => {
      if (entry) {
        allFlags[entry.key] = entry.value?.value === 'on';
      }
      return allFlags;
    }, {} as Record<string, boolean>);
  }
  return {};
};

const slice = createSlice({
  name: 'featureFlags',
  initialState: initialFeatureFlagState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(api.endpoints.getSellerInputs.matchFulfilled, (state, { payload }) => {
      if (payload.reception?.getSellerInput?.sellerInput) {
        const { featureFlagsAmplitude } = payload.reception.getSellerInput.sellerInput;

        const flagsMap = mapFlags(featureFlagsAmplitude);

        Object.entries(featureFlagMap).forEach(([key, responseKey]) => {
          // use overrides found in local storage by feature key
          const localFlag = getFlagFromLocalStorage(responseKey);
          if (localFlag !== undefined) {
            // eslint-disable-next-line no-console
            console.log('Setting feature flag from localstorage:', key, localFlag);
            state[key] = localFlag;
            return;
          }

          if (responseKey in flagsMap) {
            state[key] = flagsMap[responseKey];
          }
        });
      }
    });

    builder.addMatcher(
      api.endpoints.getPropertyFeatureFlags.matchFulfilled,
      (state, { payload }) => {
        if (payload.variant?.getFeatureFlags) {
          const { variants } = payload.variant.getFeatureFlags;

          const flagsMap = mapVariantFlags(variants);

          Object.entries(featureFlagMap).forEach(([key, responseKey]) => {
            if (responseKey in flagsMap) {
              state[key] = flagsMap[responseKey];
            }
          });
        }
      },
    );
  },
});

export default slice.reducer;
